import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { ThumbsUp, ThumbsDown, ArrowsClockwise } from "@phosphor-icons/react";
import { OUTCOME_META } from "@/components/admin/OutcomeChip";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Panel, tooltipStyle } from "./dashUtils";

const ORDER = ["solved", "resolved_by_team", "escalated", "outstanding", "no_question", "pending"];

export function ResolutionCard({ data, onReviewed }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const res = data?.resolution;
  const slices = ORDER.map((k) => ({ key: k, name: OUTCOME_META[k].label, value: res?.counts?.[k] || 0, color: OUTCOME_META[k].color })).filter((s) => s.value);
  const review = async () => {
    setBusy(true);
    try {
      await adminApi.post("/admin/outcomes/review");
      toast.success("Reviewing quiet chats — results appear in a minute");
      setTimeout(onReviewed, 20000);
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Resolution rate" testid="resolution-card"
      aside={<button onClick={review} disabled={busy} data-testid="resolution-review-btn" title="Ask the AI to review chats that have gone quiet"
        className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#73bf44] disabled:opacity-50"><ArrowsClockwise size={13} className={busy ? "animate-spin" : ""} /> Review now</button>}>
      <div className="flex items-center gap-5">
        <div className="relative w-[132px] h-[132px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={slices.length ? slices : [{ name: "None", value: 1, color: "#1a1a1a" }]} dataKey="value" innerRadius={44} outerRadius={62} paddingAngle={slices.length > 1 ? 2 : 0} stroke="none">
                {(slices.length ? slices : [{ color: "#1a1a1a" }]).map((s, i) => <Cell key={i} fill={s.color} />)}
              </Pie>
              {slices.length > 0 && <Tooltip {...tooltipStyle} />}
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="font-heading text-2xl font-bold text-white" data-testid="resolution-rate-value">{res?.rate == null ? "—" : `${res.rate}%`}</span>
            <span className="text-[0.6rem] text-neutral-500">resolved</span>
          </div>
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          {ORDER.filter((k) => k !== "no_question").map((k) => (
            <button key={k} onClick={() => k === "outstanding" && navigate("/admin/conversations?filter=outstanding")} data-testid={`resolution-row-${k}`}
              className={`w-full flex items-center gap-2 text-xs ${k === "outstanding" ? "hover:text-white cursor-pointer" : "cursor-default"}`}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: OUTCOME_META[k].color }} />
              <span className="text-neutral-400 truncate">{OUTCOME_META[k].label}</span>
              <span className="ml-auto text-white font-semibold tabular-nums">{res?.counts?.[k] || 0}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-neutral-800 flex items-center justify-between text-xs text-neutral-500">
        <span>Nick solved on his own: <span className="text-white font-semibold" data-testid="resolution-nick-rate">{res?.nick_rate == null ? "—" : `${res.nick_rate}%`}</span></span>
        <span className="flex items-center gap-3" data-testid="customer-feedback-counts">
          <span className="flex items-center gap-1"><ThumbsUp size={13} className="text-[#73bf44]" /> {data?.customer_feedback?.up || 0}</span>
          <span className="flex items-center gap-1"><ThumbsDown size={13} className="text-amber-400" /> {data?.customer_feedback?.down || 0}</span>
        </span>
      </div>
    </Panel>
  );
}
